import { create } from 'zustand';

/**
 * Realtime lap-playback state. Playback runs on an elapsed-time clock (seconds
 * from each lap's start) so every compared lap advances by its own time base and
 * the cars visibly separate on the map as their pace differs. Kept in a
 * dedicated store so the high-frequency clock tick doesn't re-render the heavy
 * config panels.
 */
interface PlaybackState {
  /** True while the clock is running. */
  playing: boolean;
  /** Elapsed seconds since lap start (the shared playback clock). */
  elapsed: number;
  /** Longest lap duration among the loaded laps (seconds); the timeline length. */
  duration: number;
  /** Playback rate multiplier (1 = realtime). */
  speed: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Advance the clock by `dt` real seconds (scaled by `speed`); stops at the end. */
  tick: (dt: number) => void;
  /** Scrub to an absolute elapsed time. */
  seek: (t: number) => void;
  setSpeed: (s: number) => void;
  setDuration: (d: number) => void;
  reset: () => void;
}

export const usePlaybackStore = create<PlaybackState>((set, get) => ({
  playing: false,
  elapsed: 0,
  duration: 0,
  speed: 1,
  play: () => {
    // Restart from the beginning if parked at the end.
    const { elapsed, duration } = get();
    set({ playing: true, elapsed: elapsed >= duration ? 0 : elapsed });
  },
  pause: () => set({ playing: false }),
  toggle: () => (get().playing ? get().pause() : get().play()),
  tick: (dt) => {
    const { elapsed, duration, speed } = get();
    const next = elapsed + dt * speed;
    if (next >= duration) {
      set({ elapsed: duration, playing: false });
    } else {
      set({ elapsed: next });
    }
  },
  seek: (t) => set({ elapsed: Math.max(0, Math.min(t, get().duration)) }),
  setSpeed: (s) => set({ speed: s }),
  setDuration: (d) => set((st) => ({ duration: d, elapsed: Math.min(st.elapsed, d) })),
  reset: () => set({ playing: false, elapsed: 0 }),
}));
